// AlignmentBoardPage.tsx
// The officer half of the Alignment Board. Spec: md/CLASS_ALIGNMENT_PLAN.md
//
// TWO THINGS, DELIBERATELY SEPARATE, because conflating them was the first
// version's bug:
//
//   REGISTRATION  one per guild. The share link, and everyone who answered it.
//                 A player fills this in once.
//   BOARDS        comps. As many as you like, each reading that same
//                 registration, so a new board starts from the full roster
//                 rather than an empty one.
//
// THE PEOPLE LIST IS ONE ROW PER PERSON, which is the point rather than a
// layout preference: officers double-ask because the same human appears as "a
// warlock" in one place and "a shaman" in another.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "./config";
import { buildClasses, buildRoles, tallyPeople } from "./alignmentStats";
import { getZamClassIconUrl } from "./modalChrome";
import { BODY, ClassCoverage, CHOICE_COLOR, DISPLAY, GhostButton, Glass, Icon, PrimaryButton, RoleCoverage, RolePill, TugOfWar, tc, useAlignmentTheme } from "./alignmentUi";

type CatalogItem = {
  item_key: string; label: string; parent_key: string | null;
  role_key: string | null; color: string | null; image_url?: string | null;
  metadata: { is_secondary?: boolean } | null;
};
type Catalog = {
  classes: CatalogItem[]; specs: CatalogItem[];
  roles: CatalogItem[]; professions: CatalogItem[];
  factions: CatalogItem[]; realmTypes: CatalogItem[];
};
type Combo = {
  id: string; class_key: string; spec_key: string;
  professions: string[]; rank: number; confirmed: boolean;
};
type Person = {
  id: string; display_name: string; discord_handle: string | null;
  faction: string | null; realm_type: string | null; open_to_anything: boolean;
  in_discord: boolean | null; source: string; status: string;
  note: string | null; submitted_at: string | null;
  has_account: boolean; combos: Combo[];
};
type PoolInfo = {
  id: string; name: string; share_token: string;
  share_enabled: boolean; max_primary_professions: number;
  hero_image_url: string | null;
  /** The guild's own note on its sign up form. Null shows the general wording. */
  signup_note?: string | null;
};
type BoardSummary = {
  id: string; name: string; description: string | null;
  raids: number; seated: number;
};
type Raid = {
  id: string; name: string; size: number; targets: any; sort_order: number;
  // Sparse: only groups an officer actually named take up space.
  group_labels?: string[] | null;
};
type Placement = {
  id: string; raid_id: string; person_id: string; option_id: string;
  seat_kind: "group" | "bench"; group_index: number | null; slot_index: number | null;
  bench_index: number | null; display_name: string; class_key: string;
  spec_key: string; professions: string[]; confirmed: boolean;
  confirmed_spec_key: string | null;
};

const ROLE_COLOR: Record<string, string> = {
  tank: "#38bdf8", healer: "#34d399", melee_dps: "#fb923c", ranged_dps: "#fb923c",
};

type SortCol = "name" | "combos" | "professions" | "status";

// Native <option> ignores the parent select's colours and falls back to the
// operating system's, which renders near-white text on a white sheet in a dark
// theme. Styling each option directly is the only thing that reliably fixes it.
const OPTION_STYLE: React.CSSProperties = { background: "var(--c-surface)", color: "var(--c-ink)" };

// Sorting by status should follow the workflow, not the alphabet.
const STATUS_ORDER = ["new", "asked", "confirmed", "declined"];

function Picker({
  value, onChange, options, allLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { v: string; l: string }[];
  allLabel: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-xl border bg-[var(--c-surface)] px-3 py-3 text-sm font-medium outline-none transition focus:border-amber-400/60 ${
        value ? "border-amber-400/60 text-amber-800 dark:text-amber-100" : "border-[var(--c-line)] text-[var(--c-ink-2)] hover:border-[var(--c-line-strong)]"
      }`}
    >
      <option value="" style={OPTION_STYLE}>{allLabel}</option>
      {options.map((o) => (
        <option key={o.v} value={o.v} style={OPTION_STYLE}>{o.l}</option>
      ))}
    </select>
  );
}

function SortHeader({
  label, col, sort, setSort,
}: {
  label: string;
  col: SortCol;
  sort: { col: SortCol; dir: "asc" | "desc" };
  setSort: React.Dispatch<React.SetStateAction<{ col: SortCol; dir: "asc" | "desc" }>>;
}) {
  const active = sort.col === col;
  return (
    <button
      type="button"
      onClick={() => setSort((s) => ({
        col,
        dir: s.col === col && s.dir === "asc" ? "desc" : "asc",
      }))}
      className={`flex items-center gap-1.5 text-left text-[11px] font-bold uppercase tracking-[0.12em] transition hover:text-[var(--c-ink)] ${
        active ? "text-amber-700 dark:text-amber-300" : "text-[var(--c-ink-3)]"
      }`}
    >
      {label}
      <span className="text-[9px]">{active ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}</span>
    </button>
  );
}

export default function AlignmentBoardPage({
  guilds,
}: {
  guilds: { id: string; name: string }[];
}) {
  useAlignmentTheme();
  const [guildId, setGuildId] = useState<string>(guilds[0]?.id || "");
  const [pool, setPool] = useState<PoolInfo | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [view, setView] = useState<"people" | "comp">("people");
  const [newCompName, setNewCompName] = useState("");
  const [openBoardId, setOpenBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [profFilter, setProfFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>({ col: "name", dir: "asc" });

  const shareUrl = pool ? `${window.location.origin}/a/${pool.share_token}` : "";

  const loadPool = useCallback(async (gid: string) => {
    if (!gid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/guilds/${gid}/alignment-pool`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Could not load the registration.");
      const data = await res.json();
      setPool(data.pool);
      setNoteDraft(data.pool?.signup_note || "");
      setCatalog(data.catalog);
      setPeople(data.people || []);
      setBoards(data.boards || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // The guild list arrives from a fetch in the parent, so on a hard refresh
  // this mounts with an empty array. The useState initialiser only runs once,
  // which left guildId pinned to "" forever: loadPool returned early and the
  // page sat empty until you navigated away and back. Adopt the first guild
  // whenever the list turns up.
  useEffect(() => {
    if (!guildId && guilds.length > 0) setGuildId(guilds[0].id);
  }, [guilds, guildId]);

  useEffect(() => { loadPool(guildId); }, [guildId, loadPool]);

  const toggleShare = async () => {
    if (!pool) return;
    await fetch(`${API_URL}/alignment-pools/${pool.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ shareEnabled: !pool.share_enabled }),
    });
    loadPool(guildId);
  };

  // Straight to S3 through the same route guild logos use, so the picture
  // outlives every deploy instead of vanishing with the server's filesystem.
  const uploadHero = async (file: File) => {
    if (!pool) return;
    setUploadingHero(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("image", file);
      const res = await fetch(`${API_URL}/alignment-pools/${pool.id}/hero`, {
        method: "POST", credentials: "include", body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not upload that image.");
      await loadPool(guildId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingHero(false);
    }
  };

  // The guild's own words on its sign up form, e.g. that a vote for a side is
  // input and the guild goes where it is strongest. Empty clears it.
  const saveNote = async () => {
    if (!pool) return;
    setSavingNote(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/alignment-pools/${pool.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ signupNote: noteDraft }),
      });
      if (!res.ok) throw new Error("Could not save the note.");
      setPool({ ...pool, signup_note: noteDraft.trim() || null });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const removeHero = async () => {
    if (!pool) return;
    await fetch(`${API_URL}/alignment-pools/${pool.id}/hero`, {
      method: "DELETE", credentials: "include",
    });
    loadPool(guildId);
  };

  const createBoard = async (rawName?: string) => {
    const name = (rawName ?? newCompName).trim() || `Comp ${boards.length + 1}`;
    const res = await fetch(`${API_URL}/guilds/${guildId}/alignment-boards`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError(data.error || "Could not create that comp."); return; }
    setNewCompName("");
    await loadPool(guildId);
    setOpenBoardId(data.board.id);
    setView("comp");
  };

  const cloneBoard = async (id: string, name: string) => {
    const taken = new Set(boards.map((b) => b.name.toLowerCase()));
    let newName = `${name} copy`;
    for (let n = 2; taken.has(newName.toLowerCase()); n += 1) newName = `${name} copy ${n}`;
    const res = await fetch(`${API_URL}/guilds/${guildId}/alignment-boards?clone=${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: newName }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError(data.error || "Could not copy that comp."); return; }
    await loadPool(guildId);
    setOpenBoardId(data.board.id);
    setView("comp");
  };

  const deleteBoard = async (id: string) => {
    if (!window.confirm("Delete this comp? The people and their answers are not affected.")) return;
    await fetch(`${API_URL}/alignment-boards/${id}`, {
      method: "DELETE", credentials: "include",
    });
    if (openBoardId === id) { setOpenBoardId(null); setView("people"); }
    loadPool(guildId);
  };

  const confirmCombo = async (optionId: string, confirmed: boolean) => {
    const res = await fetch(`${API_URL}/alignment-options/${optionId}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ confirmed }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || "Could not change that.");
      return;
    }
    loadPool(guildId);
  };

  // Clicking the status already set clears it back to "new", so a mis-click is
  // undone the same way it was made. Setting goes through the outreach endpoint
  // so it lands in the history; clearing is a plain status write, because
  // "never mind" is not something anyone needs a log entry for.
  const toggleStatus = async (p: Person, status: "asked" | "declined") => {
    if (p.status === status) {
      await fetch(`${API_URL}/alignment-people/${p.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: "new" }),
      });
    } else {
      await fetch(`${API_URL}/alignment-people/${p.id}/outreach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: status }),
      });
    }
    loadPool(guildId);
  };

  const specBy = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    (catalog?.specs || []).forEach((s) => m.set(s.item_key, s));
    return m;
  }, [catalog]);
  const classBy = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    (catalog?.classes || []).forEach((c) => m.set(c.item_key, c));
    return m;
  }, [catalog]);
  const profLabel = useCallback(
    (k: string) => (catalog?.professions || []).find((p) => p.item_key === k)?.label || k,
    [catalog]
  );
  const factionOf = useCallback(
    (k: string) => (catalog?.factions || []).find((f) => f.item_key === k),
    [catalog]
  );
  const realmTypeOf = useCallback(
    (k: string) => (catalog?.realmTypes || []).find((r) => r.item_key === k),
    [catalog]
  );

  // Search matches the words an officer would actually type: a person's name or
  // handle, but also any class, spec or profession they listed, so "resto" and
  // "engineering" both find people without touching the filters.
  const haystack = useCallback((p: Person) => [
    p.display_name,
    p.discord_handle || "",
    p.faction || "",
    p.realm_type || "",
    ...p.combos.flatMap((c) => [
      classBy.get(c.class_key)?.label || c.class_key,
      specBy.get(c.spec_key)?.label || c.spec_key,
      ...c.professions.map(profLabel),
    ]),
  ].join(" ").toLowerCase(), [classBy, specBy, profLabel]);

  const split = useCallback((field: "faction" | "realm_type", key: string) =>
    people.filter((p) => p[field] === key).length, [people]);

  // How deep the guild is in each role, class and spec. The tally and the
  // shaping both live in src/alignmentStats.ts, which is also what the player
  // facing pages run, so the sidebar a player reads and this page can never
  // quietly disagree about how many healers the guild has.
  const counts = useMemo(
    () => tallyPeople(people, (specKey) => specBy.get(specKey)?.role_key),
    [people, specBy]
  );
  const roleCounts = useMemo(() => buildRoles(catalog, counts), [catalog, counts]);
  const classCounts = useMemo(() => buildClasses(catalog, counts), [catalog, counts]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = people.filter((p) => {
      if (roleFilter && !p.combos.some((c) => specBy.get(c.spec_key)?.role_key === roleFilter)) return false;
      if (classFilter && !p.combos.some((c) => c.class_key === classFilter)) return false;
      if (profFilter && !p.combos.some((c) => c.professions.includes(profFilter))) return false;
      if (q && !haystack(p).includes(q)) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = (p: Person) => {
      switch (sort.col) {
        case "combos": return p.combos.length;
        case "professions": return new Set(p.combos.flatMap((c) => c.professions)).size;
        case "status": return STATUS_ORDER.indexOf(p.status);
        default: return p.display_name.toLowerCase();
      }
    };
    return rows.sort((a, b) => {
      const ka = key(a); const kb = key(b);
      if (ka < kb) return -dir;
      if (ka > kb) return dir;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [people, roleFilter, classFilter, profFilter, search, sort, specBy, haystack]);

  if (guilds.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-8 text-center text-sm text-[var(--c-ink-3)]">
        You need to be an officer of a guild to use this.
      </div>
    );
  }

  // ---- Presentation helpers for the players page. Plain functions and
  // derived values, not hooks, so they are safe after the early return above.
  const classIcon = (classKey: string) => {
    const c = classBy.get(classKey);
    return c?.image_url || getZamClassIconUrl(c?.label);
  };
  const specIcon = (specKey: string, classKey: string) =>
    specBy.get(specKey)?.image_url || classIcon(classKey);

  const guildName = guilds.find((g) => g.id === guildId)?.name || "";
  const lockedIn = people.filter((p) => p.combos.some((c) => c.confirmed)).length;
  const flexible = people.filter((p) =>
    new Set(p.combos.map((c) => specBy.get(c.spec_key)?.role_key).filter(Boolean)).size > 1
  ).length;
  const unanswered = people.filter((p) => !p.faction || !p.realm_type).length;

  // One column template for the header and every row, so the sort controls sit
  // exactly over the thing they sort.
  const COLS = "grid grid-cols-[minmax(230px,1.3fr)_minmax(210px,1.5fr)_minmax(170px,1fr)_136px] items-center gap-4";

  const STATUS_DOT: Record<string, string> = {
    new: "#94a3b8", asked: "#fbbf24", confirmed: "#34d399", declined: "#f87171",
  };
  const STATUS_TEXT: Record<string, string> = {
    new: "New", asked: "Asked", confirmed: "Locked in", declined: "Declined",
  };

  return (
    <div className="space-y-6 text-[var(--c-ink)]" style={BODY}>
      {/* Header over the guild's own art, so this page and the sign up form read
          as one product rather than two. */}
      <div className="relative overflow-hidden rounded-3xl border border-[var(--c-line)] bg-[var(--c-bg)]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={
            pool?.hero_image_url
              ? { backgroundImage: `url(${pool.hero_image_url})` }
              : { background: "radial-gradient(120% 120% at 80% 0%, var(--c-hero-1) 0%, var(--c-hero-2) 45%, var(--c-bg) 100%)" }
          }
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--c-bg)] via-[color-mix(in_srgb,var(--c-bg)_85%,transparent)] to-[color-mix(in_srgb,var(--c-bg)_30%,transparent)]" />
        <div className="relative flex flex-wrap items-end justify-between gap-5 p-6 sm:p-8">
          <div className="min-w-0">
            {guildName && (
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-amber-700/90 dark:text-amber-300/90">{guildName}</div>
            )}
            <h1 className="mt-1 text-3xl font-bold text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>
              {pool?.name || "WoW Forever"}
            </h1>
            <p className="mt-2 text-[15px] text-[var(--c-ink-2)]">
              Who has signed up, what they would play, and where the guild is leaning.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {guilds.length > 1 && (
              <select
                value={guildId}
                onChange={(e) => {
                  setGuildId(e.target.value);
                  setOpenBoardId(null);
                  setView("people");
                }}
                className="rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-3 py-2.5 text-sm font-semibold text-[var(--c-ink)] outline-none backdrop-blur focus:border-amber-400/60"
              >
                {guilds.map((g) => <option key={g.id} value={g.id} style={OPTION_STYLE}>{g.name}</option>)}
              </select>
            )}
            {/* Two clearly separate places, because they are two different jobs:
                collecting answers, and arranging people into raids. */}
            <div className="flex rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] p-1 backdrop-blur">
              {([
                ["people", `Players · ${people.length}`],
                ["comp", `Comps · ${boards.length}`],
              ] as const).map(([v, text]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    view === v
                      ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500 shadow-[0_6px_20px_-8px_rgba(245,158,11,0.9)]"
                      : "text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]"
                  }`}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-200">
          {error}
        </div>
      )}

      {loading && !pool && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--c-line-strong)] border-t-amber-400" />
        </div>
      )}

      {view === "people" && pool && (
        <>
          {/* The share link is the ONE thing an officer comes here to do
              before anybody has signed up, so it sits first, directly under
              the header. The counts below it are for reading; this is for
              acting on, and it was buried under four panels of numbers that
              only exist once the link has already been posted. */}
          <Glass className="p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-bold text-[var(--c-ink)]" style={DISPLAY}>Sign up link</h2>
              {/* Status and action are separate on purpose. A single button
                  labelled with its CURRENT state reads as a label, so
                  clicking "Open" to close the form is a trap. */}
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{
                  color: tc(pool.share_enabled ? "#6ee7b7" : "#fca5a5"),
                  background: pool.share_enabled ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: pool.share_enabled ? "#34d399" : "#f87171" }} />
                {pool.share_enabled ? "Accepting answers" : "Closed"}
              </span>
            </div>
            <p className="mt-1 text-sm text-[var(--c-ink-3)]">
              Post this in Discord. Players sign up once, and every comp reads from the same list.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 font-mono text-[13px] text-[var(--c-ink)] outline-none focus:border-amber-400/60"
              />
              <PrimaryButton
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
                className="px-5 py-3 text-sm"
              >
                {copied ? "Copied" : "Copy link"}
              </PrimaryButton>
              <GhostButton onClick={toggleShare} className="py-3">
                {pool.share_enabled ? "Close the form" : "Reopen the form"}
              </GhostButton>
            </div>
            {!pool.share_enabled && (
              <p className="mt-2 text-sm text-rose-700/90 dark:text-rose-300/90">
                Anyone opening the link is told sign ups have stopped.
              </p>
            )}

            {/* The artwork is set once and then never touched again, so it gets
                a thumbnail and two text actions rather than the big 16:9
                preview it used to have. That panel was the same visual weight
                as the link itself while being a fraction as important. */}
            <div className="mt-5 flex items-center gap-3 border-t border-[var(--c-line)] pt-4">
              <div
                className="h-10 w-[72px] shrink-0 overflow-hidden rounded-lg border border-[var(--c-line)] bg-cover bg-center"
                style={
                  pool.hero_image_url
                    ? { backgroundImage: `url(${pool.hero_image_url})` }
                    : { background: "radial-gradient(120% 100% at 50% 0%, var(--c-hero-1) 0%, var(--c-hero-2) 45%, var(--c-surface) 100%)" }
                }
              />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[var(--c-ink-2)]">Sign up page background</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs">
                  <label
                    className={`cursor-pointer font-semibold text-amber-700/85 dark:text-amber-300/85 transition hover:text-amber-800 dark:hover:text-amber-200 ${
                      uploadingHero ? "pointer-events-none opacity-60" : ""
                    }`}
                  >
                    {uploadingHero ? "Uploading" : pool.hero_image_url ? "Replace" : "Upload art"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingHero}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadHero(f);
                        e.currentTarget.value = "";
                      }}
                    />
                  </label>
                  {pool.hero_image_url && (
                    <button
                      type="button"
                      onClick={removeHero}
                      className="font-semibold text-[var(--c-ink-3)] transition hover:text-rose-700 dark:hover:text-rose-300"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* The guild's own words to its players, shown on the intro and above
                Submit. Left empty, the form shows a general line instead. */}
            <div className="mt-4 border-t border-[var(--c-line)] pt-4">
              <label className="block">
                <span className="text-[13px] font-semibold text-[var(--c-ink-2)]">Note on the sign up form</span>
                <span className="mt-0.5 block text-xs text-[var(--c-ink-3)]">
                  Shown to everyone who opens the link. Leave empty for the general note.
                </span>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  maxLength={600}
                  rows={3}
                  placeholder="e.g. Your vote helps us decide, but we will go wherever the guild is strongest."
                  className="mt-2 w-full resize-y rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2.5 text-sm leading-relaxed text-[var(--c-ink)] outline-none transition placeholder:text-[var(--c-ink-4)] focus:border-amber-400/60 focus:ring-4 focus:ring-amber-500/10"
                />
              </label>
              <div className="mt-2 flex items-center gap-3">
                <GhostButton
                  onClick={saveNote}
                  disabled={savingNote || noteDraft.trim() === (pool.signup_note || "").trim()}
                  className="py-2 text-sm"
                >
                  {savingNote ? "Saving" : "Save note"}
                </GhostButton>
                {noteSaved && <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Saved</span>}
                <span className="ml-auto text-xs text-[var(--c-ink-4)]">{noteDraft.length}/600</span>
              </div>
            </div>
          </Glass>

          {/* At a glance. Four numbers an officer actually acts on. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              ["Signed up", people.length, "#fbbf24"],
              ["Locked in", lockedIn, "#34d399"],
              ["Play more than one role", flexible, "#38bdf8"],
              ["Missing side or realm", unanswered, unanswered > 0 ? "#f87171" : "#64748b"],
            ].map(([label, n, c]) => (
              <div key={label as string} className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
                <div className="text-3xl font-bold leading-none" style={{ ...DISPLAY, color: tc(c as string) }}>
                  {n as number}
                </div>
                <div className="mt-2 text-[13px] font-medium text-[var(--c-ink-3)]">{label as string}</div>
              </div>
            ))}
          </div>

          {people.length > 0 && (
            <RoleCoverage roles={roleCounts} total={people.length} />
          )}

          {people.length > 0 && (
            <ClassCoverage classes={classCounts} total={people.length} />
          )}

          {people.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              <TugOfWar
                title="Which side"
                left={{ label: "Alliance", n: split("faction", "alliance"), color: CHOICE_COLOR.alliance }}
                right={{ label: "Horde", n: split("faction", "horde"), color: CHOICE_COLOR.horde }}
                middle={{ label: "No preference", n: split("faction", "either") }}
                unanswered={people.filter((p) => !p.faction).length}
              />
              <TugOfWar
                title="Realm type"
                left={{ label: "PvE", n: split("realm_type", "pve"), color: CHOICE_COLOR.pve }}
                right={{ label: "PvP", n: split("realm_type", "pvp"), color: CHOICE_COLOR.pvp }}
                middle={{ label: "No preference", n: split("realm_type", "either") }}
                unanswered={people.filter((p) => !p.realm_type).length}
              />
            </div>
          )}


          {/* Toolbar. Search first, because "who can heal" and "who took
              engineering" are the questions that actually get asked, and a free
              text box answers both without hunting through three selects. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-[var(--c-ink-3)]">⌕</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, class, spec or profession"
                className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] py-3 pl-10 pr-9 text-[15px] text-[var(--c-ink)] outline-none transition placeholder:text-[var(--c-ink-4)] focus:border-amber-400/60 focus:ring-4 focus:ring-amber-500/10"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <Picker value={roleFilter} onChange={setRoleFilter} allLabel="Any role"
              options={(catalog?.roles || []).map((r) => ({ v: r.item_key, l: r.label }))} />
            <Picker value={classFilter} onChange={setClassFilter} allLabel="Any class"
              options={(catalog?.classes || []).map((c) => ({ v: c.item_key, l: c.label }))} />
            <Picker value={profFilter} onChange={setProfFilter} allLabel="Any profession"
              options={(catalog?.professions || []).map((p) => ({ v: p.item_key, l: p.label }))} />
            {(search || roleFilter || classFilter || profFilter) && (
              <GhostButton
                onClick={() => { setSearch(""); setRoleFilter(""); setClassFilter(""); setProfFilter(""); }}
                className="py-3"
              >
                Clear
              </GhostButton>
            )}
          </div>

          <Glass className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className={`${COLS} border-b border-[var(--c-line)] px-5 py-3`}>
                  <SortHeader label="Player" col="name" sort={sort} setSort={setSort} />
                  <SortHeader label="Would play" col="combos" sort={sort} setSort={setSort} />
                  <SortHeader label="Professions" col="professions" sort={sort} setSort={setSort} />
                  <div className="flex items-center justify-between">
                    <SortHeader label="Status" col="status" sort={sort} setSort={setSort} />
                    <span className="text-[11px] font-semibold text-[var(--c-ink-3)]">{shown.length}/{people.length}</span>
                  </div>
                </div>

                {shown.length === 0 && (
                  <div className="px-5 py-16 text-center">
                    <div className="text-lg font-bold text-[var(--c-ink)]" style={DISPLAY}>
                      {people.length === 0 ? "Nobody has signed up yet" : "Nobody matches that"}
                    </div>
                    <p className="mt-1 text-sm text-[var(--c-ink-3)]">
                      {people.length === 0
                        ? "Copy the sign up link above and post it in your Discord."
                        : "Try a different search, or clear the filters."}
                    </p>
                  </div>
                )}

                {shown.map((p) => {
                  const allProfs = Array.from(new Set(p.combos.flatMap((c) => c.professions)));
                  const isOpen = openPerson === p.id;
                  const first = p.combos[0];
                  const firstCls = first ? classBy.get(first.class_key) : undefined;
                  const statusKey = p.status in STATUS_DOT ? p.status : "new";
                  return (
                    <div key={p.id} className="border-t border-[var(--c-line-soft)] first:border-t-0">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpenPerson(isOpen ? null : p.id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpenPerson(isOpen ? null : p.id); }}
                        className={`${COLS} cursor-pointer px-5 py-3.5 transition ${
                          isOpen ? "bg-[var(--c-sunken)]" : "hover:bg-[var(--c-sunken)]"
                        }`}
                      >
                        {/* Player */}
                        <div className="flex min-w-0 items-center gap-3">
                          {first ? (
                            <Icon
                              src={specIcon(first.spec_key, first.class_key)}
                              alt={p.display_name}
                              size={44}
                              ring={firstCls?.color}
                            />
                          ) : (
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-amber-400/40 bg-amber-400/10 text-lg font-bold text-amber-800 dark:text-amber-200">
                              ?
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="truncate text-[15px] font-semibold text-[var(--c-ink)]">{p.display_name}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              {p.faction && (
                                <span
                                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                                  style={{
                                    color: tc(CHOICE_COLOR[p.faction] || "#cbd5e1"),
                                    background: `${CHOICE_COLOR[p.faction] || "#cbd5e1"}1f`,
                                  }}
                                >
                                  {factionOf(p.faction)?.label}
                                </span>
                              )}
                              {p.realm_type && (
                                <span className="rounded-full border border-[var(--c-line)] px-2 py-0.5 text-[11px] font-medium text-[var(--c-ink-2)]">
                                  {realmTypeOf(p.realm_type)?.label}
                                </span>
                              )}
                              {p.open_to_anything && (
                                <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-200">
                                  You choose
                                </span>
                              )}
                              {p.discord_handle && (
                                <span className="truncate text-xs text-[var(--c-ink-3)]">@{p.discord_handle}</span>
                              )}
                              {p.source === "officer" && !p.has_account && (
                                <span className="text-[11px] text-[var(--c-ink-3)]">added by officer</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Would play: icons in preference order */}
                        <div className="flex items-center gap-1.5">
                          {p.combos.length === 0 && (
                            <span className="text-sm text-[var(--c-ink-3)]">
                              {p.open_to_anything ? "Officers choose" : "Nothing picked"}
                            </span>
                          )}
                          {p.combos.slice(0, 6).map((c, i) => {
                            const spec = specBy.get(c.spec_key);
                            const cls = classBy.get(c.class_key);
                            return (
                              <span key={c.id} title={`${i + 1}. ${spec?.label} ${cls?.label}${c.confirmed ? " (locked in)" : ""}`}>
                                <Icon
                                  src={specIcon(c.spec_key, c.class_key)}
                                  alt={spec?.label || ""}
                                  size={i === 0 ? 34 : 28}
                                  ring={c.confirmed ? "#34d399" : null}
                                />
                              </span>
                            );
                          })}
                          {p.combos.length > 6 && (
                            <span className="ml-1 text-xs font-semibold text-[var(--c-ink-3)]">+{p.combos.length - 6}</span>
                          )}
                        </div>

                        {/* Professions */}
                        <div className="flex flex-wrap items-center gap-1">
                          {allProfs.length === 0 && <span className="text-sm text-[var(--c-ink-4)]">None listed</span>}
                          {allProfs.slice(0, 3).map((k) => (
                            <span key={k} className="rounded-full bg-[var(--c-sunken)] px-2 py-0.5 text-[11px] font-medium text-[var(--c-ink-2)]">
                              {profLabel(k)}
                            </span>
                          ))}
                          {allProfs.length > 3 && (
                            <span className="text-[11px] font-semibold text-[var(--c-ink-3)]">+{allProfs.length - 3}</span>
                          )}
                        </div>

                        {/* Status */}
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                            style={{ color: tc(STATUS_DOT[statusKey]), background: `${STATUS_DOT[statusKey]}1f` }}
                          >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_DOT[statusKey] }} />
                            {STATUS_TEXT[statusKey]}
                          </span>
                          <span className={`text-[var(--c-ink-3)] transition ${isOpen ? "rotate-180" : ""}`}>▾</span>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-[var(--c-line-soft)] bg-[var(--c-surface)] px-5 py-5">
                          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
                            <div>
                              <div className="mb-3 text-sm font-bold text-[var(--c-ink)]" style={DISPLAY}>
                                Order of preference
                              </div>
                              {p.combos.length === 0 ? (
                                <p className="text-sm text-[var(--c-ink-3)]">
                                  {p.open_to_anything
                                    ? "No preference. Put them wherever the guild is short."
                                    : "Nothing picked yet."}
                                </p>
                              ) : (
                                <ul className="space-y-2">
                                  {p.combos.map((c) => {
                                    const spec = specBy.get(c.spec_key);
                                    const cls = classBy.get(c.class_key);
                                    return (
                                      <li
                                        key={c.id}
                                        className={`flex flex-wrap items-center gap-3 rounded-2xl border p-2.5 ${
                                          c.confirmed ? "border-emerald-400/40 bg-emerald-400/[0.07]" : "border-[var(--c-line)] bg-[var(--c-bg-veil)]"
                                        }`}
                                      >
                                        <span
                                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                            c.rank === 1
                                              ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500"
                                              : "border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]"
                                          }`}
                                          style={DISPLAY}
                                        >
                                          {c.rank}
                                        </span>
                                        <Icon src={specIcon(c.spec_key, c.class_key)} alt={spec?.label || ""} size={36} ring={cls?.color} />
                                        <div className="min-w-0 flex-1">
                                          <div className="text-[15px] font-semibold" style={{ color: tc(cls?.color || undefined) }}>
                                            {spec?.label} {cls?.label}
                                          </div>
                                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                            <RolePill
                                              roleKey={spec?.role_key || null}
                                              label={(catalog?.roles || []).find((r) => r.item_key === spec?.role_key)?.label || ""}
                                            />
                                            {c.professions.map((k) => (
                                              <span key={k} className="rounded-full bg-[var(--c-sunken)] px-2 py-0.5 text-[11px] font-medium text-[var(--c-ink-2)]">
                                                {profLabel(k)}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => confirmCombo(c.id, !c.confirmed)}
                                          className={`rounded-xl border px-3.5 py-2 text-xs font-bold transition ${
                                            c.confirmed
                                              ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-800 dark:text-emerald-100 hover:bg-emerald-400/25"
                                              : "border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken-2)]"
                                          }`}
                                        >
                                          {c.confirmed ? "✓ Locked in" : "Lock this in"}
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>

                            <div className="space-y-4">
                              <div>
                                <div className="mb-2 text-sm font-bold text-[var(--c-ink)]" style={DISPLAY}>Outreach</div>
                                {/* Toggles. Clicking the one already set clears
                                    it back to new, so a mis-click is undone the
                                    same way it was made. */}
                                <div className="flex flex-wrap gap-2">
                                  {(["asked", "declined"] as const).map((a) => {
                                    const on = p.status === a;
                                    return (
                                      <button
                                        key={a}
                                        type="button"
                                        onClick={() => toggleStatus(p, a)}
                                        className="inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition"
                                        style={
                                          on
                                            ? { color: STATUS_DOT[a], borderColor: `${STATUS_DOT[a]}99`, background: `${STATUS_DOT[a]}1f` }
                                            : { color: "rgba(255,255,255,0.75)", borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)" }
                                        }
                                      >
                                        {on ? "✓ " : ""}{STATUS_TEXT[a]}
                                      </button>
                                    );
                                  })}
                                </div>
                                {p.status === "confirmed" && (
                                  <p className="mt-2 text-xs text-emerald-700/80 dark:text-emerald-300/80">Locked in on one of their picks.</p>
                                )}
                              </div>
                              {p.note && (
                                <div>
                                  <div className="mb-2 text-sm font-bold text-[var(--c-ink)]" style={DISPLAY}>Their note</div>
                                  <p className="border-l-2 border-amber-400/60 pl-3 text-sm leading-relaxed text-[var(--c-ink-2)]">
                                    {p.note}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Glass>

          {/* Comps live behind a tab, so the players view has to point at them.
              Without this an officer lands here, sees a roster, and never
              discovers the half of the tool that arranges it. */}
          <Glass className="flex flex-wrap items-center gap-4 p-6">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-[var(--c-ink)]" style={DISPLAY}>
                {boards.length > 0
                  ? `${boards.length} ${boards.length === 1 ? "comp" : "comps"} built from this list`
                  : "Ready to build a comp?"}
              </h2>
              <p className="mt-1 text-sm text-[var(--c-ink-3)]">
                Arrange these players into raids of 10, 20 or 40. Make as many as you
                like and compare them. Everyone here is available in every one.
              </p>
            </div>
            <PrimaryButton onClick={() => setView("comp")}>
              {boards.length > 0 ? "Open comps" : "Build a comp"}
            </PrimaryButton>
          </Glass>
        </>
      )}

      {view === "comp" && pool && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {boards.map((b) => (
              <button key={b.id} onClick={() => setOpenBoardId(b.id)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  openBoardId === b.id
                    ? "border-amber-500/70 bg-amber-500/10 text-[var(--c-ink)]"
                    : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]"
                }`}>
                {b.name}
                <span className="ml-2 text-[11px] text-[var(--c-ink-3)]">{b.seated} seated</span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-3">
            <span className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400">New comp</span>
            <input
              value={newCompName}
              onChange={(e) => setNewCompName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createBoard(); }}
              placeholder={`Comp ${boards.length + 1}`}
              className="min-w-0 flex-1 rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-amber-500/60"
            />
            <button
              onClick={() => createBoard()}
              className="rounded-md bg-[var(--c-accent)] px-4 py-2 text-sm font-semibold text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)]"
            >
              Create
            </button>
          </div>

          {!openBoardId && (
            <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-8 text-center">
              <div className="text-lg font-semibold text-[var(--c-ink)]">
                {boards.length ? "Pick a comp" : "No comps yet"}
              </div>
              <p className="mx-auto mt-2 max-w-md text-sm text-[var(--c-ink-3)]">
                A comp is a saved arrangement of your registered players into raids
                of 10, 20 or 40. Make as many as you like and compare them. Everyone
                who registered is available in every one.
              </p>
            </div>
          )}

          {openBoardId && (
            <CompEditor
              key={openBoardId}
              boardId={openBoardId}
              people={people}
              catalog={catalog}
              onChanged={() => loadPool(guildId)}
              onClone={(name) => cloneBoard(openBoardId, name)}
              onDelete={() => deleteBoard(openBoardId)}
              onError={setError}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// One comp: its raids, each drawn as groups of five plus a bench.
//
// DRAG AND DROP IS THE PLANNER'S, not the HTML5 API: mousedown records an
// origin, a window level mousemove tracks the cursor for the ghost, and mouseup
// hit tests the pointer against every registered drop zone using
// getBoundingClientRect. Copied deliberately so seating here feels identical to
// seating a raid on the planner, including the part where dragging a player off
// every drop zone takes them out of the comp.
// ============================================================================
type DragOrigin = {
  // "pool" comes from the rail and has no placement yet. "seat" is already in
  // the comp, whether that is a group slot or a bench.
  kind: "pool" | "seat";
  personId: string;
  optionId: string;
  placementId?: string;
  name: string;
  spec: string;
  color?: string;
  icon?: string | null;
};

// Click the name, type, Enter or blur to save, Escape to abandon. Used for the
// comp, each raid, and each group of five, so renaming works the same way
// everywhere rather than each level inventing its own control.
function InlineName({
  value, onSave, className, placeholder,
}: {
  value: string;
  onSave: (next: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    else setDraft(value);
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        title="Click to rename"
        className={`rounded px-1 text-left hover:bg-[var(--c-sunken)] ${className || ""}`}
      >
        {value || <span className="text-[var(--c-ink-4)]">{placeholder || "Name"}</span>}
      </button>
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setDraft(value); setEditing(false); }
      }}
      // A rename control sits inside a drag surface, so the mousedown must not
      // reach the seat underneath and start a drag.
      onMouseDown={(e) => e.stopPropagation()}
      className={`min-w-0 rounded border border-amber-500/60 bg-[var(--c-surface)] px-1 outline-none ${className || ""}`}
    />
  );
}

function CompEditor({
  boardId, people, catalog, onChanged, onClone, onDelete, onError,
}: {
  boardId: string;
  people: Person[];
  catalog: Catalog | null;
  onChanged: () => void;
  onClone: (name: string) => void;
  onDelete: () => void;
  onError: (m: string | null) => void;
}) {
  const [board, setBoard] = useState<{ id: string; name: string; description: string | null } | null>(null);
  const [raids, setRaids] = useState<Raid[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [loading, setLoading] = useState(true);

  const [drag, setDrag] = useState<DragOrigin | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  // Finding people in the rail. With forty sign ups a flat scroll was the
  // bottleneck of the whole planner, so these mirror the players page.
  const [railSearch, setRailSearch] = useState("");
  const [railRole, setRailRole] = useState("");
  const [railClass, setRailClass] = useState("");
  const [railSort, setRailSort] = useState<"flex" | "name" | "picks">("flex");

  // Every drop zone registers itself under a key that says exactly where it is,
  // so mouseup can resolve a point to a destination without the zones needing
  // to know anything about the drag.
  const zones = useRef<Map<string, HTMLElement>>(new Map());
  const zoneRef = (key: string) => (el: HTMLElement | null) => {
    if (el) zones.current.set(key, el);
    else zones.current.delete(key);
  };
  // The return type is explicit because control flow analysis cannot see the
  // assignment inside the forEach callback, and narrows `hit` to null without it.
  const zoneAt = (x: number, y: number): string | null => {
    let hit: string | null = null;
    zones.current.forEach((el, key) => {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) hit = key;
    });
    return hit;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/alignment-boards/${boardId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not load that comp.");
      const data = await res.json();
      setBoard(data.board);
      setRaids(data.raids || []);
      setPlacements(data.placements || []);
    } catch (e: any) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [boardId, onError]);

  useEffect(() => { load(); }, [load]);

  const seatOf = (raidId: string, g: number, s: number) =>
    placements.find((p) => p.raid_id === raidId && p.seat_kind === "group"
      && p.group_index === g && p.slot_index === s);
  const benchOf = (raidId: string) =>
    placements.filter((p) => p.raid_id === raidId && p.seat_kind === "bench")
      .sort((a, b) => (a.bench_index ?? 0) - (b.bench_index ?? 0));

  const place = useCallback(async (
    raidId: string, kind: "group" | "bench", personId: string, optionId: string,
    g?: number, s?: number
  ) => {
    onError(null);
    const res = await fetch(`${API_URL}/alignment-boards/${boardId}/placements`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ raidId, personId, optionId, seatKind: kind, groupIndex: g, slotIndex: s }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { onError(data.error || "Could not seat that player."); return; }
    load(); onChanged();
  }, [boardId, load, onChanged, onError]);

  const unseat = useCallback(async (placementId: string) => {
    await fetch(`${API_URL}/alignment-placements/${placementId}`, {
      method: "DELETE", credentials: "include",
    });
    load(); onChanged();
  }, [load, onChanged]);

  // Creates the combo, then the officer drags it like any other.
  const assignSpec = async (personId: string, specKey: string) => {
    const res = await fetch(`${API_URL}/alignment-people/${personId}/options`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ specKey }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      onError(d.error || "Could not assign that spec.");
      return;
    }
    onChanged();
  };

  const renameBoard = async (name: string) => {
    const res = await fetch(`${API_URL}/alignment-boards/${boardId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      onError(d.error || "Could not rename that comp.");
      return;
    }
    load(); onChanged();
  };

  const patchRaid = async (raidId: string, body: any) => {
    const res = await fetch(`${API_URL}/alignment-raids/${raidId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      onError(d.error || "Could not save that.");
      return;
    }
    load();
  };

  // Group labels are stored as a sparse array on the raid, so "Group 3" only
  // takes up space once somebody actually names it.
  const renameGroup = (raid: Raid, index: number, name: string) => {
    const labels = Array.isArray(raid.group_labels) ? [...raid.group_labels] : [];
    while (labels.length <= index) labels.push("");
    labels[index] = name;
    patchRaid(raid.id, { groupLabels: labels });
  };
  const groupLabel = (raid: Raid, index: number) =>
    (Array.isArray(raid.group_labels) && raid.group_labels[index]) || `Group ${index + 1}`;

  // The drag itself. Same shape as PlannerPage: listeners live only while a
  // drag is in flight and are torn down with it.
  useEffect(() => {
    if (!drag) return;
    const move = (e: MouseEvent) => {
      setDragPos({ x: e.clientX, y: e.clientY });
      const key = zoneAt(e.clientX, e.clientY);
      setHoverKey((prev) => (prev === key ? prev : key));
    };
    const up = (e: MouseEvent) => {
      const key = zoneAt(e.clientX, e.clientY);
      if (key) {
        const parts = key.split(":");
        if (parts[0] === "seat") {
          const [, raidId, g, s] = parts;
          const existing = seatOf(raidId, Number(g), Number(s));
          // Dropping someone back where they already are should not cost a
          // round trip, and must not read as a change.
          if (!(existing && existing.person_id === drag.personId)) {
            place(raidId, "group", drag.personId, drag.optionId, Number(g), Number(s));
          }
        } else if (parts[0] === "bench") {
          place(parts[1], "bench", drag.personId, drag.optionId);
        }
      } else if (drag.kind === "seat" && drag.placementId) {
        // Dropped on nothing. On the planner that means "out of the raid", and
        // it means the same here.
        unseat(drag.placementId);
      }
      setDrag(null);
      setDragPos(null);
      setHoverKey(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, placements, place, unseat]);

  const specBy = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    (catalog?.specs || []).forEach((s) => m.set(s.item_key, s));
    return m;
  }, [catalog]);
  const classBy = useMemo(() => {
    const m = new Map<string, CatalogItem>();
    (catalog?.classes || []).forEach((c) => m.set(c.item_key, c));
    return m;
  }, [catalog]);

  // Spec art first, then the class icon, so a spec without its own picture
  // still shows something recognisable rather than a bare colour bar.
  const iconFor = (specKey: string, classKey: string) => {
    const cls = classBy.get(classKey);
    return specBy.get(specKey)?.image_url || cls?.image_url || getZamClassIconUrl(cls?.label);
  };

  const placedPersonIds = useMemo(
    () => new Set(placements.map((p) => p.person_id)), [placements]
  );

  const addRaid = async (size: number) => {
    const res = await fetch(`${API_URL}/alignment-boards/${boardId}/raids`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ size, name: `${size} man` }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      onError(d.error || "Could not add that raid.");
      return;
    }
    load(); onChanged();
  };

  const removeRaid = async (id: string) => {
    if (!window.confirm("Remove this raid from the comp?")) return;
    await fetch(`${API_URL}/alignment-raids/${id}`, { method: "DELETE", credentials: "include" });
    load(); onChanged();
  };

  // Composition is DERIVED from the seated specs. Officers never maintain it.
  const compOf = (raidId: string) => {
    const counts: Record<string, number> = { tank: 0, healer: 0, melee_dps: 0, ranged_dps: 0 };
    placements
      .filter((p) => p.raid_id === raidId && p.seat_kind === "group")
      .forEach((p) => {
        const role = specBy.get(p.spec_key)?.role_key;
        if (role && counts[role] !== undefined) counts[role] += 1;
      });
    return counts;
  };

  const profName = (k: string) => (catalog?.professions || []).find((x) => x.item_key === k)?.label || k;
  const rolesOf = (p: Person) =>
    new Set(p.combos.map((c) => specBy.get(c.spec_key)?.role_key).filter(Boolean) as string[]);

  const available = people.filter((p) =>
    !placedPersonIds.has(p.id) && (p.combos.length > 0 || p.open_to_anything));
  const railQuery = railSearch.trim().toLowerCase();
  const filtering = !!(railQuery || railRole || railClass);
  const unplaced = available
    .filter((p) => {
      if (railRole && !p.combos.some((c) => specBy.get(c.spec_key)?.role_key === railRole)) return false;
      if (railClass && !p.combos.some((c) => c.class_key === railClass)) return false;
      if (railQuery) {
        const hay = [
          p.display_name,
          ...p.combos.flatMap((c) => [
            classBy.get(c.class_key)?.label || "",
            specBy.get(c.spec_key)?.label || "",
            ...c.professions.map(profName),
          ]),
        ].join(" ").toLowerCase();
        if (!hay.includes(railQuery)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (railSort === "name") return a.display_name.localeCompare(b.display_name);
      if (railSort === "picks") {
        return a.combos.length - b.combos.length || a.display_name.localeCompare(b.display_name);
      }
      // Most flexible first. No preference counts as more than every role,
      // because those are exactly the people who can plug whatever is left.
      const fa = a.open_to_anything ? 5 : rolesOf(a).size;
      const fb = b.open_to_anything ? 5 : rolesOf(b).size;
      return fb - fa || a.display_name.localeCompare(b.display_name);
    });

  if (loading) return <div className="text-sm text-[var(--c-ink-3)]">Loading comp.</div>;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      {/* Rail: who is still free */}
      <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-3">
        <div className="flex items-baseline justify-between text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400">
          <span>Not in this comp</span>
          <span className="normal-case tracking-normal text-[var(--c-ink-3)]">
            {filtering ? `${unplaced.length} of ${available.length}` : available.length}
          </span>
        </div>
        <p className="mt-1 text-[11px] text-[var(--c-ink-3)]">
          Drag a spec onto a seat. Drag someone off the comp to take them out.
        </p>

        <div className="mt-3 space-y-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)]">⌕</span>
            <input
              value={railSearch}
              onChange={(e) => setRailSearch(e.target.value)}
              placeholder="Name, class, spec, profession"
              className="w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] py-2 pl-8 pr-7 text-[13px] text-[var(--c-ink)] outline-none placeholder:text-[var(--c-ink-4)] focus:border-amber-400/60"
            />
            {railSearch && (
              <button
                type="button"
                onClick={() => setRailSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 [&>select]:w-full [&>select]:py-2 [&>select]:text-xs">
            <Picker value={railRole} onChange={setRailRole} allLabel="Any role"
              options={(catalog?.roles || []).map((r) => ({ v: r.item_key, l: r.label }))} />
            <Picker value={railClass} onChange={setRailClass} allLabel="Any class"
              options={(catalog?.classes || []).map((c) => ({ v: c.item_key, l: c.label }))} />
          </div>
          <div className="flex rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-0.5 text-[11px] font-semibold">
            {([
              ["flex", "Most flexible"],
              ["name", "A to Z"],
              ["picks", "Fewest picks"],
            ] as const).map(([k, text]) => (
              <button
                key={k}
                type="button"
                onClick={() => setRailSort(k)}
                className={`flex-1 rounded-md px-1.5 py-1.5 transition ${
                  railSort === k ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
                }`}
              >
                {text}
              </button>
            ))}
          </div>
        </div>
        <ul className="mt-2 max-h-[70vh] space-y-1.5 overflow-y-auto pr-1">
          {unplaced.length === 0 && (
            <li className="py-6 text-center text-xs text-[var(--c-ink-3)]">
              {available.length === 0
                ? "Everyone who signed up is already in this comp."
                : "Nobody left matches that. Try clearing a filter."}
            </li>
          )}
          {unplaced.map((p) => (
            <li key={p.id} className="rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] p-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[var(--c-ink)]">{p.display_name}</span>
                {p.open_to_anything && (
                  <span className="rounded border border-amber-500/50 bg-amber-500/10 px-1 text-[10px] text-amber-800 dark:text-amber-200">
                    you choose
                  </span>
                )}
              </div>
              {p.open_to_anything && (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) assignSpec(p.id, e.target.value); }}
                  className="mt-1 w-full rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1.5 py-1 text-[11px] text-[var(--c-ink-2)] outline-none focus:border-amber-500/60"
                >
                  <option value="" style={OPTION_STYLE}>Put them on...</option>
                  {(catalog?.specs || []).map((sp) => (
                    <option key={sp.item_key} value={sp.item_key} style={OPTION_STYLE}>
                      {classBy.get(sp.parent_key || "")?.label} {sp.label}
                    </option>
                  ))}
                </select>
              )}
              <div className="mt-1 flex flex-wrap gap-1">
                {p.combos.map((combo) => {
                  const cls = classBy.get(combo.class_key);
                  const spec = specBy.get(combo.spec_key);
                  return (
                    <div
                      key={combo.id}
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        setDragPos({ x: e.clientX, y: e.clientY });
                        setDrag({
                          kind: "pool", personId: p.id, optionId: combo.id,
                          name: p.display_name, spec: spec?.label || "",
                          icon: iconFor(combo.spec_key, combo.class_key),
                          color: cls?.color || undefined,
                        });
                      }}
                      title={`Rank ${combo.rank}. Drag onto a seat.`}
                      className="inline-flex cursor-grab select-none items-center gap-1.5 rounded border border-[var(--c-line)] py-0.5 pl-0.5 pr-2 text-[11px] hover:bg-[var(--c-sunken)] active:cursor-grabbing"
                      style={{ color: tc(cls?.color || undefined) }}
                    >
                      <Icon src={iconFor(combo.spec_key, combo.class_key)} alt={spec?.label || ""} size={18} />
                      {combo.confirmed && "✓ "}{spec?.label}
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* The comp itself */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <InlineName
            value={board?.name || ""}
            onSave={renameBoard}
            className="text-lg font-semibold text-[var(--c-ink)]"
          />
          <div className="ml-auto flex gap-2">
            {[10, 20, 40].map((n) => (
              <button key={n} onClick={() => addRaid(n)}
                className="rounded-md border border-[var(--c-line)] px-3 py-1.5 text-xs hover:bg-[var(--c-sunken)]">
                Add {n} man
              </button>
            ))}
            <button onClick={() => onClone(board?.name || "comp")}
              className="rounded-md border border-[var(--c-line)] px-3 py-1.5 text-xs hover:bg-[var(--c-sunken)]">
              Duplicate
            </button>
            <button onClick={onDelete}
              className="rounded-md border border-[var(--c-line)] px-3 py-1.5 text-xs text-rose-700 dark:text-rose-300 hover:bg-rose-500/10">
              Delete
            </button>
          </div>
        </div>

        {raids.length === 0 && (
          <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-8 text-center text-sm text-[var(--c-ink-3)]">
            Add a raid to start. A 40 is eight groups of five, a 20 is four, a 10 is two.
          </div>
        )}

        {raids.map((raid) => {
          const groups = Math.floor(raid.size / 5);
          const seated = placements.filter((p) => p.raid_id === raid.id && p.seat_kind === "group").length;
          const bench = benchOf(raid.id);
          const c = compOf(raid.id);
          const benchKey = `bench:${raid.id}`;
          return (
            <div key={raid.id} className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-4">
              <div className="flex flex-wrap items-center gap-3">
                <InlineName
                  value={raid.name}
                  onSave={(name) => patchRaid(raid.id, { name })}
                  className="font-semibold text-[var(--c-ink)]"
                />
                <span className="text-sm text-[var(--c-ink-3)]">
                  {seated}/{raid.size}
                  {bench.length > 0 && ` · ${bench.length} benched`}
                </span>
                <span className="flex gap-2 text-xs">
                  <span style={{ color: tc(ROLE_COLOR.tank) }}>{c.tank} tank</span>
                  <span style={{ color: tc(ROLE_COLOR.healer) }}>{c.healer} healer</span>
                  <span style={{ color: tc(ROLE_COLOR.melee_dps) }}>{c.melee_dps} melee</span>
                  <span style={{ color: tc(ROLE_COLOR.ranged_dps) }}>{c.ranged_dps} ranged</span>
                </span>
                <button onClick={() => removeRaid(raid.id)}
                  className="ml-auto rounded border border-[var(--c-line)] px-2 py-1 text-xs text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]">
                  Remove
                </button>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: groups }, (_, g) => (
                  <div key={g} className="rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] p-2">
                    <InlineName
                      value={groupLabel(raid, g)}
                      onSave={(name) => renameGroup(raid, g, name)}
                      className="mb-1 block w-full text-[11px] uppercase tracking-wider text-[var(--c-ink-3)]"
                    />
                    <ul className="space-y-1">
                      {Array.from({ length: 5 }, (_, s) => {
                        const key = `seat:${raid.id}:${g}:${s}`;
                        const seat = seatOf(raid.id, g, s);
                        const hot = drag && hoverKey === key;
                        if (!seat) {
                          return (
                            <li key={s} ref={zoneRef(key) as any}>
                              <div className={`flex h-[42px] w-full items-center justify-center rounded border border-dashed text-[10px] font-medium uppercase tracking-widest transition ${
                                hot
                                  ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-800 dark:text-cyan-200"
                                  : "border-[var(--c-line)] bg-[var(--c-bg-veil)] text-[var(--c-ink-4)]"
                              }`}>
                                {hot ? "Drop" : "Empty"}
                              </div>
                            </li>
                          );
                        }
                        const cls = classBy.get(seat.class_key);
                        const spec = specBy.get(seat.spec_key);
                        const contradicts = seat.confirmed_spec_key
                          && seat.confirmed_spec_key !== seat.spec_key;
                        return (
                          <li key={s} ref={zoneRef(key) as any}>
                            <div
                              onMouseDown={(e) => {
                                if (e.button !== 0) return;
                                e.preventDefault();
                                setDragPos({ x: e.clientX, y: e.clientY });
                                setDrag({
                                  kind: "seat", personId: seat.person_id, optionId: seat.option_id,
                                  placementId: seat.id, name: seat.display_name,
                                  icon: iconFor(seat.spec_key, seat.class_key),
                                  spec: spec?.label || "", color: cls?.color || undefined,
                                });
                              }}
                              title={contradicts
                                ? `${seat.display_name} is confirmed as something else`
                                : "Drag to move, or off the comp to remove"}
                              className={`relative flex h-[42px] w-full cursor-grab select-none items-center gap-2 overflow-hidden rounded border bg-[var(--c-surface)] px-2 transition-all active:cursor-grabbing ${
                                // The card being dragged hides entirely, so the
                                // ghost under the cursor is the only copy, same
                                // as the planner.
                                drag?.placementId === seat.id ? "opacity-0" : "opacity-100"
                              } ${
                                hot ? "border-cyan-500/60 ring-1 ring-cyan-500/30"
                                    : contradicts ? "border-amber-500/60"
                                    : "border-[var(--c-line)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)]"
                              }`}
                            >
                              <Icon
                                src={iconFor(seat.spec_key, seat.class_key)}
                                alt={spec?.label || ""}
                                size={28}
                                ring={cls?.color}
                              />
                              <span className="min-w-0 flex-1 leading-tight">
                                <span
                                  className="block truncate text-[11px] font-semibold"
                                  style={{ color: tc(cls?.color || undefined) }}
                                >
                                  {seat.display_name}
                                </span>
                                <span className="block truncate text-[10px] text-[var(--c-ink-3)]">
                                  {spec?.label}
                                </span>
                              </span>
                              {contradicts && (
                                <span
                                  className="shrink-0 text-[10px] font-bold text-amber-700 dark:text-amber-400"
                                  title="Confirmed as something else"
                                >
                                  !
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

              <div
                ref={zoneRef(benchKey) as any}
                className={`mt-3 rounded-md border border-dashed p-2 transition ${
                  drag && hoverKey === benchKey
                    ? "border-cyan-500/60 bg-cyan-500/10"
                    : "border-[var(--c-line)] bg-[var(--c-surface)]"
                }`}
              >
                <div className="mb-1 text-[11px] uppercase tracking-wider text-[var(--c-ink-3)]">
                  Bench · {bench.length}
                </div>
                <div className="flex flex-wrap gap-1">
                  {bench.length === 0 && (
                    <span className="text-[11px] text-[var(--c-ink-4)]">Drag someone here to bench them.</span>
                  )}
                  {bench.map((b) => {
                    const cls = classBy.get(b.class_key);
                    const spec = specBy.get(b.spec_key);
                    return (
                      <div
                        key={b.id}
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          setDragPos({ x: e.clientX, y: e.clientY });
                          setDrag({
                            kind: "seat", personId: b.person_id, optionId: b.option_id,
                            placementId: b.id, name: b.display_name,
                            icon: iconFor(b.spec_key, b.class_key),
                            spec: spec?.label || "", color: cls?.color || undefined,
                          });
                        }}
                        title="Drag into a group to promote, or off the comp to remove"
                        className={`flex h-[34px] cursor-grab select-none items-center gap-2 rounded border bg-[var(--c-surface)] px-2 transition-all active:cursor-grabbing ${
                          drag?.placementId === b.id ? "opacity-0" : "opacity-100"
                        } border-[var(--c-line)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)]`}
                      >
                        <Icon
                          src={iconFor(b.spec_key, b.class_key)}
                          alt={spec?.label || ""}
                          size={22}
                          ring={cls?.color}
                        />
                        <span className="text-[11px] font-semibold" style={{ color: tc(cls?.color || undefined) }}>
                          {b.display_name}
                        </span>
                        <span className="text-[10px] text-[var(--c-ink-3)]">{spec?.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* The ghost, following the cursor. Same treatment as the planner. */}
      {drag && dragPos && (
        <div className="pointer-events-none fixed z-[9999]" style={{ left: dragPos.x + 10, top: dragPos.y + 10 }}>
          <div className="flex h-[42px] min-w-[140px] items-center gap-2 rounded border border-cyan-500/50 bg-[var(--c-surface)] px-2 shadow-2xl shadow-black/70 ring-1 ring-cyan-500/20">
            <Icon src={drag.icon} alt={drag.spec} size={28} ring={drag.color} />
            <span className="leading-tight">
              <span className="block text-[11px] font-semibold" style={{ color: tc(drag.color) }}>{drag.name}</span>
              <span className="block text-[10px] text-[var(--c-ink-3)]">{drag.spec}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
